/**
 * ImportDialog — CSV import wizard shared by all five 11E.9 entities.
 *
 * Steps: pick file -> dry-run preview (first 20 rows + full error list) ->
 * confirm commit -> summary (created/skipped/errored + downloadable error
 * CSV). The SAME File object is resubmitted at the commit step — the
 * backend is stateless between dry-run and commit (see
 * docs/04-modules/imports.md for why), so there is no upload-twice UX cost
 * as long as the user doesn't pick a different file mid-flow.
 */

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Modal, Button, Badge } from '@/components/common';
import {
  useDryRunImport,
  useCommitImport,
  downloadTemplate,
  type DryRunResult,
  type CommitResult,
} from '../api/imports';

const Instructions = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 12px;
  line-height: 1.6;
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
`;

const StepDot = styled.div<{ $active: boolean; $done: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $active, $done, theme }) =>
    $done ? (theme.colors.success ?? '#22c55e') : $active ? theme.colors.primary : theme.colors.backgroundTertiary};
  color: ${({ $active, $done, theme }) => ($done || $active ? '#fff' : theme.colors.textSecondary)};
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SummaryChip = styled.span<{ $color?: string }>`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $color }) => ($color ? `${$color}20` : '#f1f5f9')};
  color: ${({ $color }) => $color ?? '#64748b'};
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 600;
`;

const Td = styled.td<{ $error?: boolean }>`
  padding: 6px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: top;
  color: ${({ $error, theme }) => ($error ? theme.colors.error : 'inherit')};
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const ResultRow = styled.tr<{ $status: 'created' | 'skipped' | 'errored' }>`
  background: ${({ $status }) =>
    $status === 'created' ? '#f0fdf4' : $status === 'errored' ? '#fef2f2' : '#fffbeb'};
`;

function buildErrorCsv(entity: string, result: CommitResult): void {
  const rows = result.outcomes.filter((o) => o.status !== 'created');
  const headers = ['row_number', 'status', 'error'];
  const lines = [
    headers.join(','),
    ...rows.map((o) =>
      [o.row_number, o.status, JSON.stringify(o.error ?? '')].join(','),
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${entity}_import_errors.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  entity: string;
  entityLabel: string;
}

type Step = 1 | 2 | 3;

export const ImportDialog: React.FC<Props> = ({ isOpen, onClose, entity, entityLabel }) => {
  const [step, setStep] = useState<Step>(1);
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dryRunMutation = useDryRunImport(entity);
  const commitMutation = useCommitImport(entity);

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setDryRun(null);
    dryRunMutation.reset();
    commitMutation.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const handleDryRun = async () => {
    if (!file) return;
    const result = await dryRunMutation.mutateAsync(file);
    setDryRun(result);
    setStep(2);
  };

  const handleCommit = async () => {
    if (!file) return;
    await commitMutation.mutateAsync(file);
    setStep(3);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Import ${entityLabel}`}
      size="xl"
      data-testid={`candeza-${entity}-import-dialog`}
    >
      <StepIndicator>
        {([1, 2, 3] as Step[]).map((s) => (
          <StepDot key={s} $active={step === s} $done={step > s}>
            {step > s ? '✓' : s}
          </StepDot>
        ))}
        <span style={{ fontSize: 13, color: '#64748b', alignSelf: 'center' }}>
          {step === 1 ? 'Upload' : step === 2 ? 'Preview' : 'Results'}
        </span>
      </StepIndicator>

      {step === 1 && (
        <>
          <Instructions>
            Upload a CSV file for {entityLabel.toLowerCase()}. Each row creates a NEW record —
            duplicates by natural key (and any existing record) are reported as row errors, never
            overwritten.{' '}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => downloadTemplate(entity)}
              data-testid={`candeza-${entity}-download-template-btn`}
            >
              Download template
            </Button>
          </Instructions>
          <FileRow>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,text/csv"
              onChange={handleFileChange}
              data-testid={`candeza-${entity}-import-file-input`}
            />
          </FileRow>
          {dryRunMutation.isError && (
            <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>
              Could not parse this file. Check the format and try again.
            </p>
          )}
          <ModalActions>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button
              data-testid={`candeza-${entity}-dry-run-btn`}
              disabled={!file || dryRunMutation.isPending}
              onClick={handleDryRun}
            >
              {dryRunMutation.isPending ? 'Checking…' : 'Preview'}
            </Button>
          </ModalActions>
        </>
      )}

      {step === 2 && dryRun && (
        <>
          {dryRun.header_errors.length > 0 ? (
            <div data-testid={`candeza-${entity}-import-preview`}>
              <p style={{ color: '#ef4444', fontWeight: 600 }}>
                This file can&apos;t be imported — zero rows were written:
              </p>
              <ul style={{ color: '#ef4444', fontSize: 13 }}>
                {dryRun.header_errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
              <ModalActions>
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              </ModalActions>
            </div>
          ) : (
            <div data-testid={`candeza-${entity}-import-preview`}>
              <SummaryRow>
                <SummaryChip $color="#22c55e">{dryRun.valid_rows} valid</SummaryChip>
                <SummaryChip $color="#ef4444">{dryRun.error_rows} errors</SummaryChip>
                <SummaryChip>{dryRun.total_rows} total</SummaryChip>
              </SummaryRow>
              <div style={{ overflowX: 'auto' }}>
                <PreviewTable>
                  <thead>
                    <tr>
                      <Th>#</Th>
                      <Th>Preview</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {dryRun.preview.map((row) => (
                      <tr key={row.row_number}>
                        <Td>{row.row_number}</Td>
                        <Td>
                          {Object.entries(row.preview).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                        </Td>
                        <Td $error={row.status === 'invalid'}>
                          {row.status === 'invalid' ? row.errors.join('; ') : '✓ valid'}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </PreviewTable>
              </div>
              {dryRun.total_rows > dryRun.preview.length && (
                <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
                  Showing first {dryRun.preview.length} of {dryRun.total_rows} rows.
                </p>
              )}
              {dryRun.error_rows > 0 && (
                <>
                  <p style={{ fontSize: 13, fontWeight: 600, marginTop: 16 }}>All row errors</p>
                  <ul style={{ fontSize: 12, color: '#ef4444', maxHeight: 160, overflowY: 'auto' }}>
                    {dryRun.all_errors.map((row) => (
                      <li key={row.row_number}>Row {row.row_number}: {row.errors.join('; ')}</li>
                    ))}
                  </ul>
                </>
              )}
              <ModalActions>
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button
                  data-testid={`candeza-${entity}-confirm-commit-btn`}
                  disabled={dryRun.valid_rows === 0 || commitMutation.isPending}
                  onClick={handleCommit}
                >
                  {commitMutation.isPending
                    ? 'Importing…'
                    : `Import ${dryRun.valid_rows} record${dryRun.valid_rows !== 1 ? 's' : ''}`}
                </Button>
              </ModalActions>
            </div>
          )}
        </>
      )}

      {step === 3 && commitMutation.data && (
        <div data-testid={`candeza-${entity}-import-summary`}>
          <SummaryRow>
            <SummaryChip $color="#22c55e">{commitMutation.data.created} created</SummaryChip>
            <SummaryChip $color="#f59e0b">{commitMutation.data.skipped} skipped</SummaryChip>
            {commitMutation.data.errored > 0 && (
              <SummaryChip $color="#ef4444">{commitMutation.data.errored} errored</SummaryChip>
            )}
          </SummaryRow>
          <div style={{ overflowX: 'auto', maxHeight: 320 }}>
            <PreviewTable>
              <thead>
                <tr>
                  <Th>Row</Th>
                  <Th>Status</Th>
                  <Th>Detail</Th>
                </tr>
              </thead>
              <tbody>
                {commitMutation.data.outcomes.map((o) => (
                  <ResultRow key={o.row_number} $status={o.status}>
                    <Td>{o.row_number}</Td>
                    <Td>
                      <Badge
                        variant={o.status === 'created' ? 'success' : o.status === 'errored' ? 'error' : 'warning'}
                        size="sm"
                      >
                        {o.status}
                      </Badge>
                    </Td>
                    <Td $error={o.status === 'errored'}>
                      {o.status === 'created' ? (o.note ?? o.id) : (o.error ?? o.note ?? '—')}
                    </Td>
                  </ResultRow>
                ))}
              </tbody>
            </PreviewTable>
          </div>
          <ModalActions>
            {(commitMutation.data.skipped > 0 || commitMutation.data.errored > 0) && (
              <Button
                variant="outline"
                onClick={() => buildErrorCsv(entity, commitMutation.data as CommitResult)}
                data-testid={`candeza-${entity}-download-errors-btn`}
              >
                Download error CSV
              </Button>
            )}
            <Button onClick={handleClose}>Done</Button>
          </ModalActions>
        </div>
      )}
    </Modal>
  );
};
